import { threeDStudioStore } from '../index'

const SUBSCRIPTION_QUOTA_KEYS = [
  'studioAccessTier',
  'monthlyGenerationsAvailable',
  'monthlyGenerationsUsed',
  'extraGenerationsAvailable',
  'extraGenerationsUsed',
  'subscriptionResetDate',
  'concurrencyCap',
]

const setSubscriptionState = (patch) => {
  threeDStudioStore.setState((state) => ({
    subscription: { ...state.subscription, ...patch },
  }))
}

/**
 * Merges a quota payload (from the generator-run channel) into the subscription slice
 * in a single setState. Only whitelisted keys with defined values are applied.
 * @param {object|undefined|null} quota
 */
export const applySubscriptionQuota = (quota) => {
  if (!quota) {
    return
  }

  const patch = {}
  SUBSCRIPTION_QUOTA_KEYS.forEach((key) => {
    if (quota[key] !== undefined) {
      patch[key] = quota[key]
    }
  })

  if (Object.keys(patch).length === 0) {
    return
  }

  setSubscriptionState(patch)
}

/**
 * Hydrates the subscription slice in a single setState.
 * - `overwrite` fields are always applied.
 * - `preserveIfSet` fields are only applied when the current value is null
 *   (so a previous hydration path is not clobbered).
 * @param {{ overwrite?: object, preserveIfSet?: object }} [patches]
 */
export const hydrateSubscription = ({ overwrite = {}, preserveIfSet = {} } = {}) => {
  threeDStudioStore.setState((state) => {
    const current = state.subscription
    const preserved = {}
    Object.entries(preserveIfSet).forEach(([key, value]) => {
      if (current[key] === null) {
        preserved[key] = value
      }
    })

    return {
      subscription: { ...current, ...overwrite, ...preserved },
    }
  })
}

/**
 * Sets the current AI Studio access tier
 * @param {'not_subscribed'|'eligible_for_trial'|'in_free_beta'|'subscribed'|null} studioAccessTier
 */
export const setStudioAccessTier = (studioAccessTier) => {
  setSubscriptionState({ studioAccessTier })
}

/**
 * Sets the monthly generations available quota
 * @param {number|null} monthlyGenerationsAvailable - Total monthly generations granted to the user
 */
export const setMonthlyGenerationsAvailable = (monthlyGenerationsAvailable) => {
  setSubscriptionState({ monthlyGenerationsAvailable })
}

/**
 * Sets the monthly generations used count
 * @param {number|null} monthlyGenerationsUsed - Count of monthly generations already consumed
 */
export const setMonthlyGenerationsUsed = (monthlyGenerationsUsed) => {
  setSubscriptionState({ monthlyGenerationsUsed })
}

/**
 * Sets the extra generations available quota
 * @param {number|null} extraGenerationsAvailable - Additional generations granted beyond the monthly quota
 */
export const setExtraGenerationsAvailable = (extraGenerationsAvailable) => {
  setSubscriptionState({ extraGenerationsAvailable })
}

/**
 * Sets the extra generations used count
 * @param {number|null} extraGenerationsUsed - Count of extra generations already consumed
 */
export const setExtraGenerationsUsed = (extraGenerationsUsed) => {
  setSubscriptionState({ extraGenerationsUsed })
}

/**
 * Sets the AI Studio subscription current price (pre-formatted string, e.g. "$9.99")
 * @param {string|null} subscriptionPrice
 */
export const setSubscriptionPrice = (subscriptionPrice) => {
  setSubscriptionState({ subscriptionPrice })
}

/**
 * Sets the AI Studio subscription original (pre-discount) price
 * @param {string|null} subscriptionOriginalPrice
 */
export const setSubscriptionOriginalPrice = (subscriptionOriginalPrice) => {
  setSubscriptionState({ subscriptionOriginalPrice })
}

/**
 * Sets the pre-formatted reset date shown in the usage modal (e.g. "May 12")
 * @param {string|null} subscriptionResetDate
 */
export const setSubscriptionResetDate = (subscriptionResetDate) => {
  setSubscriptionState({ subscriptionResetDate })
}

/**
 * Sets the pre-formatted extra-generations pack price (e.g. "$9.99")
 * @param {string|null} extraGenerationsPrice
 */
export const setExtraGenerationsPrice = (extraGenerationsPrice) => {
  setSubscriptionState({ extraGenerationsPrice })
}

/**
 * Sets the monthly generations included in the AI Studio subscription plan
 * @param {number|null} subscriptionMonthlyGenerations
 */
export const setSubscriptionMonthlyGenerations = (subscriptionMonthlyGenerations) => {
  setSubscriptionState({ subscriptionMonthlyGenerations })
}

/**
 * Sets the monthly model downloads included in the AI Studio subscription plan
 * @param {number|null} subscriptionMonthlyModels
 */
export const setSubscriptionMonthlyModels = (subscriptionMonthlyModels) => {
  setSubscriptionState({ subscriptionMonthlyModels })
}

/**
 * Sets the absolute URL of the subscription payment form. The route is on the
 * `subscriptions` subdomain in production, so links from www must use the
 * server-resolved URL instead of a bare path.
 * @param {string|null} subscriptionPaymentFormUrl
 */
export const setSubscriptionPaymentFormUrl = (subscriptionPaymentFormUrl) => {
  setSubscriptionState({ subscriptionPaymentFormUrl })
}
